/*
  最近見た商品（自作）
  sections/cc-recently-viewed.liquid ／ templates/search.recently-viewed.liquid とセット。

  流れ
    1. 商品ページを開いたら、その商品IDを localStorage の先頭に足す（重複は先頭へ移動・最大20件）
    2. いま見ている商品を除いて、設定件数ぶんのIDを取り出す
    3. /search?q=id:A OR id:B&type=product&view=recently-viewed を1回だけ叩いてカードのHTMLを受け取る
    4. 検索は「見た順」で返してくれないので、IDの順に並べ直してから差し込む
    5. カードが1枚以上入ったら hidden を外す（履歴が空なら見出しごと出さない）

  保存先はお客様のブラウザだけ。サーバーには何も送らない。
  localStorage が使えない環境（プライベートモード等）では、何も表示せず素通りする。
*/
(function () {
  const STORAGE_KEY = 'cc-recently-viewed';
  const MAX_STORED = 20;

  function readHistory() {
    try {
      const raw = window.localStorage.getItem(STORAGE_KEY);
      if (!raw) return [];
      const list = JSON.parse(raw);
      if (!Array.isArray(list)) return [];
      return list.filter((id) => typeof id === 'number' && id > 0);
    } catch (e) {
      return [];
    }
  }

  function writeHistory(list) {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(list.slice(0, MAX_STORED)));
    } catch (e) {
      /* 保存できなくても表示は続ける */
    }
  }

  /* いま見ている商品を履歴の先頭に置き、更新後の履歴を返す */
  function remember(productId) {
    const list = readHistory();
    if (!productId) return list;
    const next = list.filter((id) => id !== productId);
    next.unshift(productId);
    writeHistory(next);
    return next;
  }

  function debounce(fn, wait) {
    let timer;
    return function () {
      clearTimeout(timer);
      timer = setTimeout(fn, wait);
    };
  }

  class RecentlyViewedProducts extends HTMLElement {
    connectedCallback() {
      this.list = this.querySelector('.cc-rv__list');
      this.prevButton = this.querySelector('.cc-rv__arrow--prev');
      this.nextButton = this.querySelector('.cc-rv__arrow--next');
      if (!this.list) return;

      const currentId = parseInt(this.dataset.currentId, 10) || 0;
      const limit = parseInt(this.dataset.limit, 10) || 10;

      const ids = remember(currentId)
        .filter((id) => id !== currentId)
        .slice(0, limit);

      if (ids.length === 0) return;

      this.load(ids);
    }

    load(ids) {
      const query = ids.map((id) => 'id:' + id).join(' OR ');
      const url =
        (this.dataset.searchUrl || '/search') +
        '?q=' +
        encodeURIComponent(query) +
        '&type=product&view=recently-viewed';

      fetch(url)
        .then((response) => {
          if (!response.ok) throw new Error(response.status);
          return response.text();
        })
        .then((html) => this.render(html, ids))
        .catch(() => {
          /* 取れなかったときは何も出さない（見出しも出さない） */
        });
    }

    render(html, ids) {
      const parsed = new DOMParser().parseFromString(html, 'text/html');
      const cards = new Map();
      parsed.querySelectorAll('.cc-rv__item').forEach((item) => {
        cards.set(item.dataset.productId, item);
      });

      /* 見た順に並べ直す（検索の結果は順番を保証しないため） */
      const fragment = document.createDocumentFragment();
      ids.forEach((id) => {
        const card = cards.get(String(id));
        if (card) fragment.appendChild(card);
      });

      if (!fragment.childElementCount) return;

      this.list.appendChild(fragment);
      this.removeAttribute('hidden');
      this.setupArrows();
    }

    setupArrows() {
      if (!this.prevButton || !this.nextButton) return;

      const update = () => {
        this.placeArrows();
        this.updateArrows();
      };

      this.prevButton.addEventListener('click', () => this.scrollByPage(-1));
      this.nextButton.addEventListener('click', () => this.scrollByPage(1));
      this.list.addEventListener('scroll', () => this.updateArrows(), { passive: true });
      window.addEventListener('resize', debounce(update, 150));

      update();

      /* カードの大きさが決まる（＝画像の読み込みやCSSの適用が終わる）のを待って測り直す。
         その場で1回測るだけだと、レイアウトが確定する前の値を拾ってしまう。 */
      const media = this.list.querySelector('.card__inner');
      if (media && 'ResizeObserver' in window) {
        this.observer = new ResizeObserver(() => update());
        this.observer.observe(media);
      }
    }

    disconnectedCallback() {
      if (this.observer) this.observer.disconnect();
    }

    /* 矢印を「画像の縦中央」に置く。
       カード下部の商品名の行数は端末によって変わるので、割合ではなく画像の実寸から決める。 */
    placeArrows() {
      const media = this.list.querySelector('.card__inner');
      if (!media) return;
      const height = media.getBoundingClientRect().height;
      if (height > 0) {
        this.style.setProperty('--cc-rv-arrow-top', Math.round(height / 2) + 'px');
      }
    }

    scrollByPage(direction) {
      this.list.scrollBy({ left: direction * this.list.clientWidth, behavior: 'smooth' });
    }

    updateArrows() {
      /* 端の判定は小数の誤差が出るので少し余裕を持たせる */
      const slack = 2;
      const maxScroll = this.list.scrollWidth - this.list.clientWidth;
      const scrollable = maxScroll > slack;

      this.prevButton.hidden = !scrollable || this.list.scrollLeft <= slack;
      this.nextButton.hidden = !scrollable || this.list.scrollLeft >= maxScroll - slack;
    }
  }

  if (!customElements.get('recently-viewed-products')) {
    customElements.define('recently-viewed-products', RecentlyViewedProducts);
  }
})();
